import React, { useState } from 'react';
import dateFormat from 'dateformat';
import { Badge, Flex, Text } from '@chakra-ui/react';
import { OutlineCard } from '../MySubmission/PreferencesTab';
import { ButtonText, useToast } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faUpload,
  faDownload
} from '@fortawesome/pro-regular-svg-icons';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { EditorialUploadCopyrightFormModal } from '../CopyrightFormSubmittingAuthorOnly/EditorialUploadCopyrightForm';
import { OverwriteCopyrightAgreementModal } from '../CopyrightFormSubmittingAuthorOnly/OverwriteCopyrightModal';
import { EditorialRequestCopyrightFormModal } from '../CopyrightFormSubmittingAuthorOnly/EditorialRequestCopyright';
import { downloadFile } from 'src/utils/file';
import ProtectedWorkflowAction from '../../ProtectedAction/ProtectedWorkflowAction';
import { useSubmissionFields } from '../../../hooks/useSubmissionFields';
import { FormFieldSubmittingAuthor } from '@publishing-engine/domain/types/article';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  fetchSubmittingAuthorOnlyCopyrightForm,
  requestSubmittingAuthorOnlyCopyrightForm
} from '../../../api/queries';
import {
  IAuthorCopyrightForm,
  IRoleType
} from '@publishing-engine/domain/models';

const CopyrightStatusDescription = ({
  isLoading,
  copyrightForm
}: {
  isLoading: boolean;
  copyrightForm?: IAuthorCopyrightForm;
}) => {
  const { availableActions } = useSubmissionData();

  if (isLoading) {
    return (
      <Text textStyle="textBody" color="gray.500">
        Loading latest status...
      </Text>
    );
  }

  if (copyrightForm) {
    const submittedCopyrightDescription = !copyrightForm.editorUpdated
      ? 'The author has provided a Copyright and Publication Agreement'
      : 'A Copyright and Publication Agreement was uploaded by the Editors';
    return (
      <Text>
        {submittedCopyrightDescription} on{' '}
        {dateFormat(copyrightForm.date, 'mmmm d, yyyy')}.
      </Text>
    );
  }

  if (availableActions.contains('EditorialRequestCopyright')) {
    return (
      <Text textStyle="textBody">
        No Copyright and Publication Agreement has been provided – request the
        Author to provide this document to proceed with publication.
      </Text>
    );
  }

  return (
    <>
      <Text textStyle="textBody">
        Copyright and Publication Agreements become available for Authors in the
        Revision stage.
      </Text>
      <Text textStyle="textBody">
        If the authors have not provided this information by the time the
        submission enters pre-production, you will be able to request the form
        entirely, or request additional edits.
      </Text>
    </>
  );
};

const CopyrightAndPublicationAgreementSubmittingAuthorOnly: React.FC = () => {
  const {
    availableActions,
    workflowResponse: {
      workflowData: { copyrightForm: copyrightFormWorkflow }
    },
    submissionId,
    submissionContextId,
    organizationId
  } = useSubmissionData();

  const [isRequestAgreementModalEnabled, setIsRequestAgreementModalEnabled] =
    useState<boolean>(false);
  const [isUploadCopyrightModalEnabled, setIsUploadCopyrightModalEnabled] =
    useState<boolean>(false);
  const [
    isOverwriteAgreementModalEnabled,
    setIsOverwriteAgreementModalEnabled
  ] = useState<'upload' | ''>('');

  const displayManualUploadButton = availableActions.contains(
    'EditorialUploadCopyright'
  );
  const displayRequestAgreementButton = availableActions.contains(
    'EditorialRequestCopyright'
  );

  const copyrightFormQueryKey = [
    'submitting-author-only-copyright-form-editorial',
    { submissionId, roleType: 'editorial' }
  ];

  const {
    data: submittingAuthorCopyRight,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: copyrightFormQueryKey,
    queryFn: () =>
      fetchSubmittingAuthorOnlyCopyrightForm(
        submissionId,
        'editorial' as IRoleType
      )
  });

  const copyrightForm = submittingAuthorCopyRight?.authorCopyrightForm.find(
    copyright => copyright.isSubmittingAuthor === true
  );

  const downloadAgreementButton = !!copyrightForm?.file?.url;

  const displayButtons =
    displayRequestAgreementButton ||
    displayManualUploadButton ||
    downloadAgreementButton;

  const { getFieldState } = useSubmissionFields();
  const submittingAuthorField =
    getFieldState<FormFieldSubmittingAuthor>('SubmittingAuthor')!;

  const toast = useToast();

  const fetchSubmissionAuthorOnlyCopyRightFormMutation = useMutation({
    mutationFn: ({
      submissionId,
      email
    }: {
      submissionId: string;
      email: string;
    }) =>
      requestSubmittingAuthorOnlyCopyrightForm(submissionId, {
        roleContext: 'editorial',
        type: 'request',
        email
      }),
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Request sent successfully',
        description: '',
        isClosable: true
      });
      setIsRequestAgreementModalEnabled(false);
    }
  });

  const sendEmailRequest = async () => {
    await fetchSubmissionAuthorOnlyCopyRightFormMutation.mutateAsync({
      submissionId,
      email: submittingAuthorField.value
    });
  };

  const handleRequestAgreement = () => {
    if (copyrightForm) {
      setIsRequestAgreementModalEnabled(true);
    } else {
      sendEmailRequest();
    }
  };

  const [isFileDownloading, setIsFileDownloading] = useState(false);

  const handleFileDownload = async (fileUrl: string) => {
    try {
      setIsFileDownloading(true); // disable button
      await downloadFile(fileUrl);
    } finally {
      setIsFileDownloading(false); // re-enable no matter what
    }
  };

  return (
    <OutlineCard>
      {isUploadCopyrightModalEnabled && (
        <EditorialUploadCopyrightFormModal
          onClose={() => setIsUploadCopyrightModalEnabled(false)}
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={submissionContextId}
          authorEmail={submittingAuthorField.value}
        />
      )}
      {isOverwriteAgreementModalEnabled && (
        <OverwriteCopyrightAgreementModal
          onClose={() => setIsOverwriteAgreementModalEnabled('')}
          onSubmit={() => {
            if (isOverwriteAgreementModalEnabled === 'upload') {
              setIsUploadCopyrightModalEnabled(true);
            }

            setIsOverwriteAgreementModalEnabled('');
          }}
        />
      )}
      {isRequestAgreementModalEnabled && (
        <EditorialRequestCopyrightFormModal
          isPending={fetchSubmissionAuthorOnlyCopyRightFormMutation.isPending}
          onSubmit={sendEmailRequest}
          onClose={() => setIsRequestAgreementModalEnabled(false)}
        />
      )}
      <Flex flexDirection="column" gap={4}>
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingGroup">
            Copyright and Publication Agreement
          </Text>
          <CopyrightStatusDescription
            isLoading={isLoading || isFetching}
            copyrightForm={copyrightForm}
          />
        </Flex>
        {displayButtons && (
          <Flex gap={6} flexWrap="wrap" alignItems="center">
            {downloadAgreementButton && (
              <Flex justifyContent="flex-start">
                <ButtonText
                  variant="secondary"
                  onClick={() => {
                    copyrightForm.file?.url &&
                      handleFileDownload(copyrightForm.file.url);
                  }}
                  isDisabled={isFileDownloading}
                  leftIcon={<FontAwesomeIcon icon={faDownload} />}
                >
                  Download Full Agreement
                </ButtonText>
              </Flex>
            )}
            {displayRequestAgreementButton &&
              (!copyrightFormWorkflow?.requested ? (
                <ProtectedWorkflowAction actionName="EditorialRequestCopyright">
                  <ButtonText
                    onClick={() => handleRequestAgreement()}
                    disabled={
                      fetchSubmissionAuthorOnlyCopyRightFormMutation.isPending
                    }
                    isLoading={
                      fetchSubmissionAuthorOnlyCopyRightFormMutation.isPending
                    }
                    iconLeft={<FontAwesomeIcon icon={faEdit} />}
                  >
                    Request Agreement
                  </ButtonText>
                </ProtectedWorkflowAction>
              ) : (
                <Badge size="base" variant="neutral">
                  <Flex gap={1} alignItems="center">
                    <FontAwesomeIcon icon={faEdit} />{' '}
                    <Text textStyle="headingItem">Request Agreement</Text>
                  </Flex>
                </Badge>
              ))}
            {displayManualUploadButton && (
              <ProtectedWorkflowAction actionName="EditorialUploadCopyright">
                <ButtonText
                  onClick={() => {
                    if (copyrightFormWorkflow?.submitted) {
                      setIsOverwriteAgreementModalEnabled('upload');
                    } else {
                      setIsUploadCopyrightModalEnabled(true);
                    }
                  }}
                  disabled={false}
                  iconLeft={<FontAwesomeIcon icon={faUpload} />}
                >
                  Upload Agreement Manually
                </ButtonText>
              </ProtectedWorkflowAction>
            )}
          </Flex>
        )}
      </Flex>
    </OutlineCard>
  );
};

export default CopyrightAndPublicationAgreementSubmittingAuthorOnly;
